import React, { PropsWithChildren } from 'react';

import { CeilingTray } from '@paragon/contract-library';
import { getModifierKeys } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../../../components/hooks';
import {
  clearHoveredTrayStatesIfActive,
  setHoveredTrayStateIfApplicable,
} from '../../../../store/actions/layout/ceilingTrayActions';
import {
  handleStandardSceneItemEditPropertiesAction,
  handleStandardSceneItemRightClick,
} from '../../../../store/actions/sceneInteractionActions';
import { selectItem } from '../../../../store/actions/selectActions';
import { ensureHoverItemIsActive } from '../../../../store/actions/viewportActions';
import { isPickingActive } from '../../../../store/selectors/pickingSelectors';
import { threeJsDom } from '../../../util/events/ThreeJSDom';

type Props = PropsWithChildren<{
  tray: CeilingTray | Omit<CeilingTray, 'guid'>;
}>;

export function CeilingTrayEventHandlers({ tray, children }: Props) {
  const dispatch = useAppDispatch();
  const isPicking = useAppSelector(isPickingActive);

  // Ceiling trays are never pickable; so to rule out any conflicts with ceiling planes its safe enough to make them
  //   non-interactive during picking.
  if (isPicking) return children;

  // An insertion ceiling tray is non-interactive.
  if (!('guid' in tray)) return children;

  return (
    <group
      onClick={(event) => {
        if (event.detail === 2) {
          event.stopPropagation();
          dispatch(handleStandardSceneItemEditPropertiesAction('ceilingTray', tray));
        } else if (threeJsDom.shouldDispatchClickEvent()) {
          event.stopPropagation();
          dispatch(selectItem(tray, 'ceilingTray', { intent: 'toggle', exclusive: !getModifierKeys(event).ctrlKey }));
        }
      }}
      onContextMenu={async (event) => {
        dispatch(handleStandardSceneItemRightClick(event, { type: 'ceilingTray', guid: tray.guid, origin: 'fiber' }));
      }}
      onPointerOver={(event) => {
        event.stopPropagation();
        dispatch(ensureHoverItemIsActive({ type: 'ceilingTray', guid: tray.guid }));
      }}
      onPointerMove={(event) => {
        event.stopPropagation();
        dispatch(setHoveredTrayStateIfApplicable(tray.guid));
      }}
      onPointerOut={() => {
        dispatch(clearHoveredTrayStatesIfActive(tray.guid));
      }}
    >
      {children}
    </group>
  );
}
